import type { LevelDefinition } from "@algoquest/schemas";
import { createCampaignLevel } from "./levelFactory";

/**
 * Phase 15 -- World 6: "Bubble Sort" (Master Spec Section 5), campaign
 * levels 51-60. Teaches: adjacent comparison, swap, keep, pass, largest/smallest migration, repeated passes, termination.
 * Algorithm(s) used in this world: bubble-sort.
 */
export const world06BubbleSortLevels: LevelDefinition[] = [
  createCampaignLevel({
    id: "campaign-051",
    world: 6,
    levelInWorld: 1,
    globalLevelNumber: 51,
    algorithm: "bubble-sort",
    values: [5, 2, 9, 1, 7, 4],
    sortDirection: "ascending",
    learningObjectives: [
      "Name this algorithm: Bubble Sort -- repeated passes of adjacent comparisons.",
      "See more than one full pass needed before the list is completely sorted.",
    ],
    notes: "A genuine multi-pass sort -- the first pass alone won't finish the job.",
  }),
  createCampaignLevel({
    id: "campaign-052",
    world: 6,
    levelInWorld: 2,
    globalLevelNumber: 52,
    algorithm: "bubble-sort",
    values: [5, 2, 9, 1, 7, 4],
    sortDirection: "descending",
    learningObjectives: [
      "Apply multi-pass Bubble Sort to a descending goal on the same values as level 51.",
      "Compare which pairs swap here versus the ascending version.",
    ],
    notes: "Same six values as level 51, sorted largest to smallest instead.",
  }),
  createCampaignLevel({
    id: "campaign-053",
    world: 6,
    levelInWorld: 3,
    globalLevelNumber: 53,
    algorithm: "bubble-sort",
    values: [3, 8, 1, 6, 2, 9, 4],
    sortDirection: "ascending",
    learningObjectives: [
      "Watch the largest remaining value migrate to its final position by the end of each pass.",
      "See why later passes can safely ignore the end of the list once a value has settled there.",
    ],
    notes: "7 items -- the largest value present reaches the far end after just the first pass.",
  }),
  createCampaignLevel({
    id: "campaign-054",
    world: 6,
    levelInWorld: 4,
    globalLevelNumber: 54,
    algorithm: "bubble-sort",
    values: [1, 3, 5, 7, 9, 11, 13],
    sortDirection: "ascending",
    learningObjectives: [
      "See a fully sorted dataset produce zero swaps across an entire pass.",
      "Understand early termination: Bubble Sort can recognize it is done without checking every possible pass.",
    ],
    notes: "Already sorted -- a single no-swap pass is enough to confirm the list is complete.",
  }),
  createCampaignLevel({
    id: "campaign-055",
    world: 6,
    levelInWorld: 5,
    globalLevelNumber: 55,
    algorithm: "bubble-sort",
    values: [13, 11, 9, 7, 5, 3, 1],
    sortDirection: "ascending",
    learningObjectives: [
      "See the worst case for Bubble Sort: every pair swaps, on every pass, until the very end.",
      "Contrast the number of passes needed here against level 54's single pass.",
    ],
    notes: "Exactly reverse-ordered -- this forces the maximum possible number of passes for 7 items.",
  }),
  createCampaignLevel({
    id: "campaign-056",
    world: 6,
    levelInWorld: 6,
    globalLevelNumber: 56,
    algorithm: "bubble-sort",
    values: [6, 14, 2, 9, 4, 11, 7, 1],
    sortDirection: "ascending",
    learningObjectives: [
      "Apply repeated passes and migration to a larger, 8-item dataset.",
      "Track how the sorted region grows from the end of the list inward with each pass.",
    ],
    notes: "An 8-item unsorted list, sorted ascending across several passes.",
  }),
  createCampaignLevel({
    id: "campaign-057",
    world: 6,
    levelInWorld: 7,
    globalLevelNumber: 57,
    algorithm: "bubble-sort",
    values: [6, 14, 2, 9, 4, 11, 7, 1],
    sortDirection: "descending",
    learningObjectives: [
      "Sort the same 8 values as level 56, this time largest to smallest.",
      "Reinforce that pass structure and termination work identically regardless of direction.",
    ],
    notes: "Same eight values as level 56, descending goal instead of ascending.",
  }),
  createCampaignLevel({
    id: "campaign-058",
    world: 6,
    levelInWorld: 8,
    globalLevelNumber: 58,
    algorithm: "bubble-sort",
    values: [2, 4, 3, 6, 5, 7, 8, 9],
    sortDirection: "ascending",
    learningObjectives: [
      "See how few swaps a nearly-sorted dataset actually needs.",
      "Recognize early termination firing after just one or two passes, not many.",
    ],
    notes: "Only one adjacent pair (4 and 3) is out of order -- the sort finishes almost immediately.",
  }),
  createCampaignLevel({
    id: "campaign-059",
    world: 6,
    levelInWorld: 9,
    globalLevelNumber: 59,
    algorithm: "bubble-sort",
    values: [17, 4, 29, 8, 15, 2, 22, 11, 6],
    sortDirection: "ascending",
    learningObjectives: [
      "Complete a full multi-pass ascending sort on the largest dataset in this world.",
      "Consolidate pass structure, migration, and early termination together.",
    ],
    notes: "World 6's largest dataset -- a genuine multi-pass ascending sort.",
  }),
  createCampaignLevel({
    id: "campaign-060",
    world: 6,
    levelInWorld: 10,
    globalLevelNumber: 60,
    algorithm: "bubble-sort",
    values: [17, 4, 29, 8, 15, 2, 22, 11, 6],
    sortDirection: "descending",
    learningObjectives: [
      "Complete the same dataset as level 59 sorted in the opposite direction.",
      "Reflect on everything Bubble Sort has taught before moving to Selection Sort.",
    ],
    notes: "World 6 capstone -- same nine values as level 59, sorted largest to smallest.",
  }),
];
